// Workbook metadata stays in the FE; invoice API DTOs do not depend on the layout.
export const INVOICE_EXCEL_VERSION_LABEL = "Phiên bản mẫu";
export const INVOICE_EXCEL_V2_FEE_HEADERS = ["Đơn giá", "Đơn vị tính", "Số lượng", "Giảm giá", "Thành tiền"];
const VALIDATION_FEE_HEADERS = ["Mã khoản", "Tên khoản", ...INVOICE_EXCEL_V2_FEE_HEADERS];
const PAYMENT_HEADERS = ["Thiếu/ Thừa phiếu thu trước", "Phải thu", "Đã thu", "Tiền mặt", "Chuyển khoản", "Thiếu thừa còn lại"];
const text = (value) => String(value == null ? "" : value).trim();
const codeKey = (value) => text(value).toUpperCase();
const nameKey = (value) => text(value).toLowerCase();
const identifier = (value) => text(value) && text(value) !== "—" && text(value) !== "-";
const labelKey = (value) => nameKey(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/g, "d").replace(/[^a-z0-9]/g, "");

function layoutError(message, row = "Cấu trúc file", value = "", field = "Mẫu Excel") {
  return { row, studentCode: "—", studentName: "—", field, value, expected: "Mẫu Excel hợp lệ và danh mục khớp dữ liệu gốc", message };
}

function headerIndex(matrix) {
  const found = matrix.slice(0, 8).findIndex((row) => (row || []).some((cell) => labelKey(cell).includes("mahocsinh")));
  return found >= 0 ? found : 3;
}

export function formatInvoiceExcelFeeHeader(fee) {
  return `${text(fee.name || fee.revenueName)}\nMã khoản: ${text(fee.revenueCode)}`;
}

function conflictingNames(students, fees) {
  const catalog = new Map(fees.map((fee) => [codeKey(fee.revenueCode), fee]));
  const conflicts = [];
  students.forEach((student) => {
    if (!identifier(student.invoiceCode)) return;
    (student.invoiceDetails || []).forEach((detail) => {
      const fee = catalog.get(codeKey(detail.revenueCode));
      if (fee && nameKey(detail.revenueName || detail.name) !== nameKey(fee.revenueName || fee.name)) {
        conflicts.push(`Mã ${fee.revenueCode}: ${student.studentCode || student.kidCode || student.invoiceCode} có tên "${text(detail.revenueName || detail.name)}"`);
      }
    });
  });
  return conflicts;
}

export function validateInvoiceExcelV2Export(students, fees) {
  const seen = new Set();
  fees.forEach((fee) => {
    if (!identifier(fee.revenueCode) || /[\r\n]/.test(text(fee.revenueCode)) || !identifier(fee.name || fee.revenueName) || !identifier(fee.idx)) {
      throw new Error("Danh mục thiếu mã, tên hoặc mã định danh khoản thu. Vui lòng tải lại dữ liệu gốc trước khi xuất file mẫu.");
    }
    if (seen.has(codeKey(fee.revenueCode))) throw new Error(`Danh mục có mã khoản thu trùng: ${fee.revenueCode}.`);
    seen.add(codeKey(fee.revenueCode));
  });
  const conflicts = conflictingNames(students, fees);
  if (conflicts.length) {
    throw new Error(`Không thể xuất file mẫu vì tên khoản trên phiếu cũ không thống nhất. ${conflicts.slice(0, 5).join("; ")}. Vui lòng thống nhất dữ liệu gốc trước khi tải file mẫu.`);
  }
}

export function readInvoiceExcelWorkbook(workbook, readRows) {
  const names = workbook.SheetNames || [];
  const feeNames = names.filter((name) => labelKey(name) === "khoanthu");
  const feeCandidates = names.filter((name) => labelKey(name).includes("khoanthu"));
  const studentNames = names.filter((name) => labelKey(name) === "danhsachhs");
  const feeName = feeNames[0] || names.find((name) => labelKey(name).includes("khoanthu"));
  const studentName = studentNames[0] || names.find((name) => name !== feeName);
  const feeRows = feeName ? readRows(workbook.Sheets[feeName]) : [];
  const matrix = studentName ? readRows(workbook.Sheets[studentName]) : [];
  const versionRow = feeRows[1] || [];
  const metadata = {
    hasFeeSheet: Boolean(feeName),
    studentSheetRecognized: studentNames.length === 1,
    versionMarker: labelKey(versionRow[0]) === labelKey(INVOICE_EXCEL_VERSION_LABEL) ? text(versionRow[1]) : "",
    hasVersionMarker: labelKey(versionRow[0]) === labelKey(INVOICE_EXCEL_VERSION_LABEL),
    merges: studentName ? (workbook.Sheets[studentName]["!merges"] || []) : [],
    errors: [],
  };
  if (feeCandidates.length > 1 || studentNames.length > 1) metadata.errors.push(layoutError("Workbook có nhiều sheet cùng vai trò; không xác định được Sheet học sinh hoặc Khoản Thu."));
  return { feeName, studentName, feeRows, matrix, metadata };
}

function validateCatalog(rows, benchmark, errors) {
  const catalog = new Map();
  rows.forEach((row) => {
    const key = codeKey(row.revenueCode);
    const where = `Sheet 'Khoản Thu' dòng ${row.rowNum}`;
    if (!identifier(row.revenueCode) || !identifier(row.revenueName) || !identifier(row.idx)) {
      errors.push(layoutError("Danh mục phải có mã khoản, tên khoản và mã định danh hợp lệ.", where, row.revenueCode));
      return;
    }
    if (catalog.has(key)) {
      errors.push(layoutError(`Mã khoản "${row.revenueCode}" bị trùng trong Sheet 'Khoản Thu'.`, where, row.revenueCode));
      return;
    }
    const system = benchmark.systemFeeMap && benchmark.systemFeeMap.get(key);
    const custom = benchmark.customFeeMap && benchmark.customFeeMap.get(key);
    const known = system || custom;
    const source = labelKey(row.source);
    const declaredCustom = source.includes("tuthem") || source.includes("tukhoitao");
    const declaredSystem = source === "tuhethong";
    if ((!declaredCustom && !declaredSystem) || (system && !declaredSystem) || (custom && !system && !declaredCustom) || (!known && !declaredCustom)) {
      errors.push(layoutError(`Nguồn gốc của khoản "${row.revenueCode}" không khớp dữ liệu gốc hoặc không hợp lệ.`, where, row.source));
    }
    if (known && (nameKey(row.revenueName) !== nameKey(known.revenueName || known.name) || text(row.idx) !== text(known.idx))) {
      errors.push(layoutError(`Tên hoặc mã định danh của khoản "${row.revenueCode}" không khớp dữ liệu gốc.`, where, row.revenueName));
    }
    catalog.set(key, {
      ...row,
      revenueCode: text(known ? known.revenueCode : row.revenueCode),
      revenueName: text(row.revenueName),
      idxReference: text(row.idx),
      isCustom: known ? !system : declaredCustom,
    });
  });
  if (!rows.length) errors.push(layoutError("File mẫu phải có danh mục khoản thu trong Sheet 'Khoản Thu'."));
  return catalog;
}

// Shared format gate for both catalog loading and student-file validation.
// Only a marked V2 workbook with five-column fee groups may enter either flow.
export function inspectInvoiceExcelV2Layout(matrix, metadata = {}) {
  matrix = matrix || [];
  metadata = metadata || {};
  const errors = [...(metadata.errors || [])];
  const rowH2Index = headerIndex(matrix);
  const rowH1Index = Math.max(0, rowH2Index - 1);
  const rowH1 = matrix[rowH1Index] || [];
  const rowH2 = matrix[rowH2Index] || [];
  if (metadata.versionMarker !== "2" || !metadata.hasVersionMarker) {
    errors.push(layoutError("File Excel không đúng định dạng. Vui lòng tải và sử dụng file mẫu Excel mới nhất."));
  }
  if (!metadata.hasFeeSheet) errors.push(layoutError("File mẫu thiếu Sheet 'Khoản Thu'."));
  if (!metadata.studentSheetRecognized) errors.push(layoutError("File mẫu phải có một Sheet 'Danh Sách HS' (hoặc tên không dấu tương ứng)."));
  let payStart = -1;
  for (let c = 17; c < rowH2.length; c++) {
    if (labelKey(rowH1[c]) === "thongtinthanhtoan" || labelKey(rowH2[c]) === labelKey(PAYMENT_HEADERS[0])) {
      payStart = c;
      break;
    }
  }
  const groupStarts = [];
  if (!errors.length) {
    if (payStart < 22 || (payStart - 17) % 5 !== 0) errors.push(layoutError("Các nhóm khoản thu phải gồm đúng năm cột, nằm trước Thông tin thanh toán."));
    if (payStart >= 17) {
      PAYMENT_HEADERS.forEach((header, offset) => {
        if (labelKey(rowH2[payStart + offset]) !== labelKey(header)) errors.push(layoutError(`Thiếu hoặc lệch cột thanh toán "${header}".`, rowH2Index + 1));
      });
    }
    for (let start = 17; start + 4 < payStart; start += 5) {
      groupStarts.push(start);
      const where = `Nhóm cột ${start + 1}`;
      INVOICE_EXCEL_V2_FEE_HEADERS.forEach((header, offset) => {
        if (labelKey(rowH2[start + offset]) !== labelKey(header)) errors.push(layoutError(`Nhóm khoản thu thiếu hoặc lệch cột "${header}".`, where));
      });
      const merged = (metadata.merges || []).some((range) => range.s.r === rowH1Index && range.e.r === rowH1Index && range.s.c === start && range.e.c === start + 4);
      if (!merged) errors.push(layoutError("Tiêu đề khoản thu phải ghép đúng năm cột của nhóm.", where));
    }
    if (!groupStarts.length) errors.push(layoutError("Không tìm thấy nhóm khoản thu hợp lệ."));
  }
  return { errors, rowH1Index, rowH2Index, rowH1, payStart, groupStarts };
}

// Reuse the business validation engine through an internal matrix only.
// Seven-column workbooks are rejected by the V2 format gate above.
export function prepareInvoiceExcelLayout(matrix, sheet2Rows, metadata = {}, benchmark = {}) {
  const structure = inspectInvoiceExcelV2Layout(matrix, metadata);
  const { errors, rowH1Index, rowH2Index, rowH1, payStart, groupStarts } = structure;
  if (errors.length) return { matrix, catalog: new Map(), errors };
  const catalog = validateCatalog(sheet2Rows, benchmark || {}, errors);
  conflictingNames((benchmark && benchmark.students) || [], [...catalog.values()]).forEach((message) => {
    errors.push(layoutError(`Tên khoản trên phiếu cũ không thống nhất. ${message}. Vui lòng thống nhất dữ liệu gốc trước khi nhập file Excel.`));
  });
  const groups = [];
  const seenCodes = new Set();
  groupStarts.forEach((start) => {
    const titleLines = text(rowH1[start]).split(/\r?\n/);
    const codeLines = titleLines.filter((line) => /^\s*Mã khoản\s*:/i.test(line));
    const code = codeLines.length === 1 ? text(codeLines[0].replace(/^\s*Mã khoản\s*:/i, "")) : "";
    const title = text(titleLines.filter((line) => !/^\s*Mã khoản\s*:/i.test(line)).join("\n"));
    const fee = catalog.get(codeKey(code));
    const where = `Nhóm cột ${start + 1} (${title || "chưa có tên"})`;
    if (!identifier(code) || !fee) errors.push(layoutError(`Mã khoản "${code || "(trống)"}" ở tiêu đề không có trong danh mục.`, where));
    if (seenCodes.has(codeKey(code))) errors.push(layoutError(`Mã khoản "${code}" xuất hiện ở nhiều nhóm cột.`, where));
    seenCodes.add(codeKey(code));
    if (fee && nameKey(title) !== nameKey(fee.revenueName)) errors.push(layoutError(`Tên tiêu đề "${title}" không khớp tên "${fee.revenueName}" trong Sheet 'Khoản Thu'.`, where));
    if (fee) groups.push({ start, fee });
  });
  if (errors.length) return { matrix, catalog, errors };
  const adapted = matrix.map((row, index) => {
    if (!row || index < rowH1Index || !row.some((cell) => text(cell) !== "")) return row ? row.slice() : [];
    const result = Array.from({ length: 17 }, (_, c) => row[c]);
    groups.forEach(({ start, fee }) => {
      if (index === rowH1Index) result.push(fee.revenueName, null, null, null, null, null, null);
      else if (index === rowH2Index) result.push(...VALIDATION_FEE_HEADERS);
      else result.push(fee.revenueCode, fee.revenueName, ...Array.from({ length: 5 }, (_, c) => row[start + c]));
    });
    result.push(...row.slice(payStart));
    return result;
  });
  return { matrix: adapted, catalog, errors };
}

export function resolveInvoiceExcelV2DetailIdx(student, fee) {
  const matches = ((student && student.invoiceDetails) || []).filter((detail) => codeKey(detail.revenueCode) === codeKey(fee.revenueCode));
  if (matches.length > 1) throw new Error(`Dữ liệu gốc của học sinh có nhiều khoản cùng mã ${fee.revenueCode}.`);
  const idx = matches.length ? matches[0].idx : fee.idxReference;
  if (!identifier(idx)) throw new Error(`Không đủ mã định danh cho khoản ${fee.revenueCode}. Vui lòng tải lại dữ liệu gốc.`);
  return text(idx);
}

export function hasCompleteInvoiceExcelV2Details(rows, detailKey) {
  return rows.every((row) => (row[detailKey] || []).length > 0 && row[detailKey].every((fee) =>
    identifier(fee.revenueCode) && identifier(fee.revenueName) && identifier(fee.idx) && typeof fee.isCustom === "boolean"
  ));
}
